import { Check } from "lucide-react";
import { ReactNode } from "react";

type AdminRadioCardOption<T extends string> = {
  value: T;
  label: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
};

type AdminRadioCardGroupProps<T extends string> = {
  value: T;
  options: AdminRadioCardOption<T>[];
  name: string;
  onChange: (value: T) => void;
  disabled?: boolean;
  columnsClassName?: string;
};

const gridColsClassMap: Record<number, string> = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4",
};

export function AdminRadioCardGroup<T extends string>({ value, options, name, onChange, disabled = false, columnsClassName }: AdminRadioCardGroupProps<T>) {
  const columns = columnsClassName || gridColsClassMap[Math.min(options.length, 4)] || "sm:grid-cols-2";
  return (
    <div className={`grid gap-3 ${columns}`}>
      {options.map((option) => {
        const checked = option.value === value;
        return (
          <label
            key={option.value}
            className={`flex min-h-[76px] cursor-pointer items-start gap-3 rounded-md border p-4 transition ${
              checked ? "border-[#9ac8aa] bg-[#f1f8f3]" : "border-[#d8dedb] bg-white hover:border-[#b8c8bf] hover:bg-[#fbfcfb]"
            } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
          >
            <input
              checked={checked}
              className="sr-only"
              disabled={disabled}
              name={name}
              type="radio"
              onChange={() => onChange(option.value)}
            />
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                checked ? "border-[#315746] bg-[#315746]" : "border-[#c3cdc7] bg-white"
              }`}
              aria-hidden="true"
            >
              {checked && <Check className="text-white" size={12} />}
            </span>
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-sm font-bold text-[#315746]">
                {option.icon}
                {option.label}
              </span>
              {option.description && <span className="mt-1 block text-xs leading-5 text-[#7b877f]">{option.description}</span>}
            </span>
          </label>
        );
      })}
    </div>
  );
}

type AdminSwitchCardProps = {
  checked: boolean;
  description?: ReactNode;
  disabled?: boolean;
  label: ReactNode;
  name?: string;
  onChange: (checked: boolean) => void;
};

export function AdminSwitchCard({ checked, description, disabled = false, label, name, onChange }: AdminSwitchCardProps) {
  return (
    <label
      className={`group grid min-h-[88px] cursor-pointer grid-cols-[minmax(0,1fr)_48px] items-start gap-3 rounded-md border px-4 py-4 transition sm:items-center ${
        checked ? "border-[#9ac8aa] bg-[#f1f8f3]" : "border-[#d8dedb] bg-white hover:border-[#b8c8bf] hover:bg-[#fbfcfb]"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <input
        checked={checked}
        className="sr-only"
        disabled={disabled}
        name={name}
        type="checkbox"
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="min-w-0 py-0.5 pr-1">
        <span className="block break-words text-sm font-bold leading-5 text-[#315746]">{label}</span>
        {description && <span className="mt-1.5 block break-words text-xs leading-5 text-[#7b877f]">{description}</span>}
      </span>
      <span
        className={`relative mt-0.5 inline-flex h-7 w-12 shrink-0 items-center justify-self-end rounded-full transition sm:mt-0 ${
          checked ? "bg-[#315746]" : "bg-[#b8c1bc] group-hover:bg-[#9da9a3]"
        }`}
        aria-hidden="true"
      >
        <span
          className={`inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#315746] shadow-sm transition ${
            checked ? "translate-x-[22px]" : "translate-x-0.5"
          }`}
        >
          {checked && <Check size={15} />}
        </span>
      </span>
    </label>
  );
}
